import { useState, useEffect, useCallback } from 'react';
import { IonIcon, IonPage, IonContent, useIonRouter } from '@ionic/react';
import { callSharp } from 'ionicons/icons';
import { useLocation } from 'react-router-dom';
import { DataTable } from '../components/shared/DataTable';
import { AddClientModal } from '../components/shared/AddClientModal';
import { CreateCallModal } from '../components/calls/CreateCallModal';
import { searchClients } from '../services/clientService';
import { useBreadcrumbStore } from '../store/useBreadcrumbStore';
import { useRefresh } from '../contexts/RefreshContext';
import { ShopsBudget } from '../components/shared/ShopsBudget';
import type { Client } from '../types/client';
import type { Column } from '../components/shared/DataTable';

type Segment = 'active' | 'demo' | 'inactive';

export default function ClientsPage() {
  const router = useIonRouter();

  const columns: Column<Client>[] = [
    {
      key: 'name',
      label: 'Name',
      render: (item) => (
        <button
          className="text-blue-600 hover:text-blue-800 hover:underline text-left font-medium"
          onClick={(e) => { e.stopPropagation(); router.push(`/client-detail?clientId=${item.id}`, 'none'); }}
        >
          {item.name}
        </button>
      ),
    },
    {
      key: 'shops',
      label: '',
      render: (item) => item.shopsTotal != null && item.shopsPlaced != null
        ? <ShopsBudget shopsPlaced={item.shopsPlaced} shopsScheduled={item.shopsScheduled ?? 0} shopsTotal={item.shopsTotal} billingStartDate={item.billingStartDate} billingEndDate={item.billingEndDate} />
        : null,
    },
  ];
  const location = useLocation();
  const [data, setData] = useState<Client[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCreateCallModal, setShowCreateCallModal] = useState(false);
  const [segment, setSegment] = useState<Segment>('active');
  const [hasInactive, setHasInactive] = useState(false);
  const setBreadcrumb = useBreadcrumbStore((s) => s.set);

  useEffect(() => {
    if (location.pathname !== '/clients') return;
    setBreadcrumb([{ label: 'Clients' }]);
  }, [location.pathname, setBreadcrumb]);

  // Check if any inactive clients exist
  useEffect(() => {
    searchClients({ status: 'inactive' }, { pageSize: 1 }).then((results) => {
      setHasInactive(results.length > 0);
    }).catch(() => {});
  }, []);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const filter: Record<string, unknown> = {};
      if (segment === 'active') {
        filter.demo = false;
        filter.status = 'active';
      } else if (segment === 'demo') {
        filter.demo = true;
      } else if (segment === 'inactive') {
        filter.status = 'inactive';
      }
      const results = await searchClients(filter, { page, pageSize, orderBy: 'updated', order: 'desc' });
      results.sort((a, b) => {
        if (a.test && !b.test) return 1;
        if (!a.test && b.test) return -1;
        return 0;
      });
      setData(results);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, segment]);

  useEffect(() => { fetch(); }, [fetch]);

  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  useEffect(() => {
    registerRefreshHandler('/clients', fetch);
    return () => unregisterRefreshHandler('/clients');
  }, [fetch, registerRefreshHandler, unregisterRefreshHandler]);

  const segmentButton = (value: Segment, label: string) => (
    <button
      key={value}
      onClick={() => { setSegment(value); setPage(1); }}
      className={`px-3 py-1.5 text-sm font-medium transition-colors ${
        segment === value
          ? 'bg-blue-600 text-white'
          : 'bg-white text-gray-700 hover:bg-gray-50'
      }`}
    >
      {label}
    </button>
  );

  return (
    <IonPage>
      <IonContent>
        <div className="p-4 sm:p-6 lg:p-8">
          <DataTable
            title="Clients"
            columns={columns}
            data={data}
            page={page}
            pageSize={pageSize}
            loading={loading}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            onRefresh={fetch}
            onAdd={() => setShowAddModal(true)}
            headerControls={
              <>
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                {segmentButton('active', 'Active Clients')}
                {segmentButton('demo', 'Demo Accounts')}
                {hasInactive && segmentButton('inactive', 'Inactive')}
              </div>
              <button
                onClick={() => setShowCreateCallModal(true)}
                className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                title="Create Call"
              >
                <IonIcon icon={callSharp} style={{ fontSize: '16px' }} />
              </button>
              </>
            }
            actions={(item) => (
              <>
                <button
                  onClick={() => router.push(`/properties?clientId=${item.id}`, 'none')}
                  className="text-xs px-2 py-1 rounded text-white transition-colors" style={{ backgroundColor: 'var(--ion-color-secondary)' }}
                >
                  Properties
                </button>
                <button
                  onClick={() => router.push(`/teams?clientId=${item.id}`, 'none')}
                  className="text-xs px-2 py-1 rounded text-white transition-colors" style={{ backgroundColor: 'var(--ion-color-secondary)' }}
                >
                  Teams
                </button>
                <button
                  onClick={() => router.push(`/agents?clientId=${item.id}`, 'none')}
                  className="text-xs px-2 py-1 rounded text-white transition-colors" style={{ backgroundColor: 'var(--ion-color-secondary)' }}
                >
                  Agents
                </button>
                <button
                  onClick={() => router.push(`/calls?clientId=${item.id}`, 'none')}
                  className="text-xs px-2 py-1 rounded text-white transition-colors" style={{ backgroundColor: 'var(--ion-color-secondary)' }}
                >
                  Calls
                </button>
              </>
            )}
          />
          {showAddModal && (
            <AddClientModal
              onClose={() => setShowAddModal(false)}
              onSuccess={() => { setShowAddModal(false); fetch(); }}
            />
          )}
          {showCreateCallModal && (
            <CreateCallModal
              clientFilter={{ demo: segment === 'demo' }}
              onClose={() => setShowCreateCallModal(false)}
              onSuccess={() => setShowCreateCallModal(false)}
            />
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
